import React, { useState, useEffect, useMemo } from 'react';
import { useOrgAuth } from '../../../contexts/OrgAuthContext';
import {
  getJobListings, moderateJobListing, removeJobListing,
  getListingApplications, createJobListing, updateJobListing,
  getAllJobApplications,
} from '../../../api/programmesApi';

const TABS = [
  { key: 'request',      label: 'Job Listings Request' },
  { key: 'live',         label: 'Job Listings' },
  { key: 'applications', label: 'Job Applications' },
];

const STATUS_BADGE = {
  pending_approval: { cls: 'bb-badge', style: { background: '#FEF9C3', color: '#92400E' },   label: 'Pending Approval' },
  live:             { cls: 'bb-badge bb-badge--green',                                         label: 'Live' },
  paused:           { cls: 'bb-badge', style: { background: '#DBEAFE', color: '#1E40AF' },    label: 'Paused' },
  closed:           { cls: 'bb-badge bb-badge--gray',                                          label: 'Closed' },
  removed:          { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' },    label: 'Removed' },
  expired:          { cls: 'bb-badge', style: { background: '#F3F4F6', color: '#6B7280' },    label: 'Expired' },
  rejected:         { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' },    label: 'Rejected' },
};

const JOB_TYPES    = ['full-time', 'part-time', 'freelance', 'volunteer'];
const ALL_STATUSES = ['live', 'paused', 'closed', 'expired', 'removed', 'rejected', 'pending_approval'];
const BLANK_FORM   = { title: '', organisation: '', jobType: '', description: '', requirements: '', deadline: '' };

const fmtDate  = (ts) => { if (!ts) return '—'; const d = ts?.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString('en-SG'); };
const sortIcon = (dir) => dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : ' ⇅';

export default function JobBoard({ orgId }) {
  const { currentUser } = useOrgAuth();
  const [activeTab, setActiveTab] = useState('request');

  const [listings,  setListings]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  // Moderation
  const [viewListing,  setViewListing]  = useState(null);
  const [reviewPanel,  setReviewPanel]  = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [moderating,   setModerating]   = useState({});
  const [appsCache,    setAppsCache]    = useState({});

  // Job Listings filters & sort
  const [typeFilter,     setTypeFilter]     = useState('all');
  const [statusFilter,   setStatusFilter]   = useState('live');
  const [postedByFilter, setPostedByFilter] = useState('all');
  const [appSort,        setAppSort]        = useState(null);   // null | 'asc' | 'desc'
  const [deadlineSort,   setDeadlineSort]   = useState(null);

  // Job Applications tab
  const [allApplications,     setAllApplications]     = useState([]);
  const [appsLoading,         setAppsLoading]         = useState(false);
  const [appsSearchText,      setAppsSearchText]      = useState('');
  const [appsListingFilter,   setAppsListingFilter]   = useState('all');

  // Create modal
  const [showCreate,    setShowCreate]    = useState(false);
  const [createForm,    setCreateForm]    = useState(BLANK_FORM);
  const [createSaving,  setCreateSaving]  = useState(false);
  const [createError,   setCreateError]   = useState('');
  const [createSuccess, setCreateSuccess] = useState(false);

  useEffect(() => { loadListings(); }, [orgId]);

  useEffect(() => {
    if (activeTab !== 'applications') return;
    setAppsLoading(true);
    getAllJobApplications(orgId)
      .then(data => setAllApplications(data))
      .catch(() => setAllApplications([]))
      .finally(() => setAppsLoading(false));
  }, [activeTab, orgId]);

  const loadListings = async () => {
    setLoading(true); setError('');
    try {
      const data = await getJobListings(orgId, 'all') || [];
      setListings(data);
      const liveRows = data.filter(l => l.status === 'live');
      const counts = {};
      await Promise.all(liveRows.map(async l => {
        try { const apps = await getListingApplications(orgId, l.id); counts[l.id] = (apps || []).length; }
        catch { counts[l.id] = 0; }
      }));
      setAppsCache(counts);
    } catch (e) {
      setError(e.message || 'Failed to load listings.');
    } finally {
      setLoading(false);
    }
  };

  const pending = listings.filter(l => l.status === 'pending_approval');

  // ── Derived filtered + sorted list for Job Listings tab ──────────
  const postedByOptions = useMemo(() =>
    [...new Set(listings.map(l => l.postedByName).filter(Boolean))].sort()
  , [listings]);

  const filteredListings = useMemo(() => {
    let rows = listings.filter(l => {
      if (typeFilter     !== 'all' && l.jobType      !== typeFilter)     return false;
      if (statusFilter   !== 'all' && l.status       !== statusFilter)   return false;
      if (postedByFilter !== 'all' && l.postedByName !== postedByFilter) return false;
      return true;
    });
    if (appSort) {
      rows = [...rows].sort((a, b) => {
        const diff = (appsCache[a.id] ?? 0) - (appsCache[b.id] ?? 0);
        return appSort === 'asc' ? diff : -diff;
      });
    } else if (deadlineSort) {
      rows = [...rows].sort((a, b) => {
        const da = a.deadline ? new Date(a.deadline?.toDate ? a.deadline.toDate() : a.deadline) : new Date(0);
        const db = b.deadline ? new Date(b.deadline?.toDate ? b.deadline.toDate() : b.deadline) : new Date(0);
        return deadlineSort === 'asc' ? da - db : db - da;
      });
    }
    return rows;
  }, [listings, typeFilter, statusFilter, postedByFilter, appSort, deadlineSort, appsCache]);

  const cycleSort = (current, setter, otherSetter) => {
    otherSetter(null);
    setter(current === null ? 'asc' : current === 'asc' ? 'desc' : null);
  };

  const appsListingOptions = useMemo(() =>
    [...new Set(allApplications.map(a => a.listingTitle).filter(Boolean))].sort()
  , [allApplications]);

  const filteredApplications = useMemo(() => {
    return allApplications.filter(a => {
      if (appsListingFilter !== 'all' && a.listingTitle !== appsListingFilter) return false;
      if (appsSearchText) {
        const q = appsSearchText.toLowerCase();
        if (
          !a.memberName?.toLowerCase().includes(q) &&
          !a.memberEmail?.toLowerCase().includes(q) &&
          !a.listingTitle?.toLowerCase().includes(q) &&
          !a.organisation?.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [allApplications, appsListingFilter, appsSearchText]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleApprove = async (listing) => {
    setModerating(p => ({ ...p, [listing.id]: true }));
    try {
      await moderateJobListing(orgId, listing.id, true, currentUser.uid, currentUser.name || 'Admin');
      setReviewPanel(null);
      await loadListings();
    } catch (e) { setError(e.message || 'Failed to approve.'); }
    finally { setModerating(p => ({ ...p, [listing.id]: false })); }
  };

  const handleReject = async (listing) => {
    if (!rejectReason.trim()) { setError('A rejection reason is required.'); return; }
    setModerating(p => ({ ...p, [listing.id]: true }));
    try {
      await moderateJobListing(orgId, listing.id, false, currentUser.uid, currentUser.name || 'Admin', rejectReason);
      setReviewPanel(null); setRejectReason('');
      await loadListings();
    } catch (e) { setError(e.message || 'Failed to reject.'); }
    finally { setModerating(p => ({ ...p, [listing.id]: false })); }
  };

  const handleModerateAction = async (listingId, action) => {
    setModerating(p => ({ ...p, [listingId]: true }));
    try {
      await updateJobListing(orgId, listingId, { status: action });
      await loadListings();
    } catch (e) { setError(e.message || `Failed to ${action} listing.`); }
    finally { setModerating(p => ({ ...p, [listingId]: false })); }
  };

  const handleRemoveSubmit = async () => {
    if (!removeReason.trim()) { setError('A removal reason is required.'); return; }
    setModerating(p => ({ ...p, [removeTarget]: true }));
    try {
      await removeJobListing(orgId, removeTarget, removeReason, currentUser.uid);
      setRemoveTarget(null); setRemoveReason('');
      await loadListings();
    } catch (e) { setError(e.message || 'Failed to remove listing.'); }
    finally { setModerating(p => ({ ...p, [removeTarget]: false })); }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError(''); setCreateSaving(true);
    try {
      // Admin bypasses approval — create then immediately approve to go live
      const listingId = await createJobListing(orgId, createForm, currentUser.uid, currentUser.name || 'Admin');
      await moderateJobListing(orgId, listingId, true, currentUser.uid, currentUser.name || 'Admin');
      setCreateSuccess(true);
      setCreateForm(BLANK_FORM);
      await loadListings();
      setTimeout(() => { setShowCreate(false); setCreateSuccess(false); }, 1800);
    } catch (err) {
      setCreateError(err.message || 'Failed to create listing.');
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <>
      {/* Details Modal */}
      {viewListing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 560, width: '100%', boxShadow: '0 24px 72px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: '0 0 6px' }}>{viewListing.title}</h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => { const b = STATUS_BADGE[viewListing.status] || STATUS_BADGE.pending_approval; return <span className={b.cls} style={b.style}>{b.label}</span>; })()}
                  <span style={{ fontSize: 13, color: '#6B7280', textTransform: 'capitalize' }}>{viewListing.jobType || '—'}</span>
                </div>
              </div>
              <button className="bb-btn-outline-sm" onClick={() => setViewListing(null)}>✕ Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: 13, marginBottom: 20, background: '#F9FAFB', borderRadius: 8, padding: '12px 16px' }}>
              <div><span style={{ color: '#6B7280' }}>Company</span><br /><strong>{viewListing.organisation || '—'}</strong></div>
              <div><span style={{ color: '#6B7280' }}>Job Type</span><br /><strong style={{ textTransform: 'capitalize' }}>{viewListing.jobType || '—'}</strong></div>
              <div><span style={{ color: '#6B7280' }}>Approval to List</span><br /><strong>{viewListing.approvalToList || '—'}</strong></div>
              <div><span style={{ color: '#6B7280' }}>Expiry Date</span><br /><strong>{viewListing.expiryDate || fmtDate(viewListing.deadline) || '—'}</strong></div>
              <div><span style={{ color: '#6B7280' }}>Posted</span><br /><strong>{fmtDate(viewListing.createdAt)}</strong></div>
              <div><span style={{ color: '#6B7280' }}>Application Deadline</span><br /><strong>{fmtDate(viewListing.deadline) || '—'}</strong></div>
            </div>
            {viewListing.description && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B46C1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Job Description</div>
                <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>{viewListing.description}</p>
              </div>
            )}
            {viewListing.requirements && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B46C1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Experience & Requirements</div>
                <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>{viewListing.requirements}</p>
              </div>
            )}
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 16, marginTop: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B46C1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Contact Details</div>
              {viewListing.contactDetails ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', fontSize: 13 }}>
                  <div><span style={{ color: '#6B7280' }}>Name</span><br /><strong>{viewListing.contactDetails.name || viewListing.postedByName || '—'}</strong></div>
                  <div><span style={{ color: '#6B7280' }}>Email</span><br /><strong>{viewListing.contactDetails.email || '—'}</strong></div>
                  {viewListing.contactDetails.phone && <div><span style={{ color: '#6B7280' }}>Phone</span><br /><strong>{viewListing.contactDetails.phone}</strong></div>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#6B7280' }}>{viewListing.postedByName || viewListing.postedBy || '—'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove Modal */}
      {removeTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0 }}>Remove Listing</h3>
            <div className="bb-form-group">
              <label>Reason for Removal <span className="bb-required">*</span></label>
              <textarea className="bb-textarea" rows={3} value={removeReason} onChange={e => setRemoveReason(e.target.value)} placeholder="Why is this listing being removed?" />
            </div>
            <div className="bb-editor-actions">
              <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={handleRemoveSubmit}>Confirm Remove</button>
              <button className="bb-btn-outline-sm" onClick={() => { setRemoveTarget(null); setRemoveReason(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Listing Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 600, width: '100%', boxShadow: '0 24px 72px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Create Job Listing</h3>
              <button className="bb-btn-outline-sm" onClick={() => { setShowCreate(false); setCreateForm(BLANK_FORM); setCreateError(''); setCreateSuccess(false); }}>✕ Close</button>
            </div>
            {createSuccess && <div className="bb-alert bb-alert--success" style={{ marginBottom: 16 }}>Listing created and published successfully.</div>}
            {createError   && <div className="bb-alert bb-alert--error"   style={{ marginBottom: 16 }}>{createError}</div>}
            <form onSubmit={handleCreateSubmit}>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Job Title <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="bb-input" value={createForm.title}
                    onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Part-time Admin Coordinator" required />
                </div>
                <div className="bb-form-group">
                  <label>Organisation <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="bb-input" value={createForm.organisation}
                    onChange={e => setCreateForm(f => ({ ...f, organisation: e.target.value }))}
                    placeholder="Hiring organisation name" required />
                </div>
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Job Type <span style={{ color: '#DC2626' }}>*</span></label>
                  <select className="bb-select" value={createForm.jobType}
                    onChange={e => setCreateForm(f => ({ ...f, jobType: e.target.value }))} required>
                    <option value="">— select type —</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="freelance">Freelance</option>
                    <option value="volunteer">Volunteer</option>
                  </select>
                </div>
                <div className="bb-form-group">
                  <label>Application Deadline</label>
                  <input className="bb-input" type="date" value={createForm.deadline}
                    onChange={e => setCreateForm(f => ({ ...f, deadline: e.target.value }))} />
                </div>
              </div>
              <div className="bb-form-group">
                <label>Job Description <span style={{ color: '#DC2626' }}>*</span></label>
                <textarea className="bb-textarea" rows={4} value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the role, responsibilities, and what the ideal candidate looks like…" required />
              </div>
              <div className="bb-form-group">
                <label>Requirements</label>
                <textarea className="bb-textarea" rows={3} value={createForm.requirements}
                  onChange={e => setCreateForm(f => ({ ...f, requirements: e.target.value }))}
                  placeholder="List any qualifications, experience, or skills required…" />
              </div>
              <div className="bb-editor-actions">
                <button type="submit" className="bb-btn-primary" disabled={createSaving}>
                  {createSaving ? 'Publishing…' : 'Publish Listing'}
                </button>
                <button type="button" className="bb-btn-outline-sm"
                  onClick={() => { setShowCreate(false); setCreateForm(BLANK_FORM); setCreateError(''); setCreateSuccess(false); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bb-editor">
        {/* Tab nav */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 24 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === t.key ? '2px solid #6B46C1' : '2px solid transparent',
              color: activeTab === t.key ? '#6B46C1' : '#6B7280',
              fontWeight: activeTab === t.key ? 600 : 400, fontSize: 14,
            }}>
              {t.label}
              {t.key === 'request' && pending.length > 0 && (
                <span style={{ marginLeft: 6, background: '#EF4444', color: '#fff', borderRadius: 9999, fontSize: 11, padding: '1px 6px' }}>{pending.length}</span>
              )}
              {t.key === 'applications' && allApplications.length > 0 && (
                <span style={{ marginLeft: 6, background: '#6B46C1', color: '#fff', borderRadius: 9999, fontSize: 11, padding: '1px 6px' }}>{allApplications.length}</span>
              )}
            </button>
          ))}
        </div>

        {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Review Panel */}
        {reviewPanel && (
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Review: {reviewPanel.title}</h3>
              <button className="bb-btn-outline-sm" onClick={() => { setReviewPanel(null); setRejectReason(''); }}>Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 14 }}>
              <div><strong>Organisation:</strong> {reviewPanel.organisation || '—'}</div>
              <div><strong>Job Type:</strong> {reviewPanel.jobType || '—'}</div>
              <div><strong>Posted By:</strong> {reviewPanel.postedByName || reviewPanel.postedBy || '—'}</div>
              <div><strong>Posted:</strong> {fmtDate(reviewPanel.createdAt)}</div>
              <div><strong>Deadline:</strong> {fmtDate(reviewPanel.deadline)}</div>
            </div>
            {reviewPanel.description && (
              <div style={{ marginBottom: 16 }}>
                <strong style={{ fontSize: 13 }}>Description:</strong>
                <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{reviewPanel.description}</p>
              </div>
            )}
            {reviewPanel.requirements && (
              <div style={{ marginBottom: 16 }}>
                <strong style={{ fontSize: 13 }}>Requirements:</strong>
                <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{reviewPanel.requirements}</p>
              </div>
            )}
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
              <div className="bb-form-group">
                <label>Rejection Reason <span className="bb-hint">(required if rejecting)</span></label>
                <textarea className="bb-textarea" rows={2} value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Explain why this listing is being rejected." />
              </div>
              <div className="bb-editor-actions">
                <button className="bb-btn-primary" disabled={moderating[reviewPanel.id]} onClick={() => handleApprove(reviewPanel)}>Approve Listing</button>
                <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} disabled={moderating[reviewPanel.id]} onClick={() => handleReject(reviewPanel)}>Reject Listing</button>
              </div>
            </div>
          </div>
        )}

        {/* ── JOB LISTINGS REQUEST tab ── */}
        {activeTab === 'request' && (
          <div>
            <div className="bb-editor-header">
              <h2 style={{ margin: 0 }}>Job Listings Request</h2>
              <p className="bb-editor-desc">Review and approve or reject job listing submissions before they go live.</p>
            </div>
            {loading ? (
              <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading…</td></tr></tbody></table>
            ) : pending.length === 0 ? (
              <div className="bb-empty-state">No listings pending approval.</div>
            ) : (
              <table className="bb-table">
                <thead>
                  <tr><th>Title</th><th>Organisation</th><th>Posted By</th><th>Job Type</th><th>Posted</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {pending.map(l => (
                    <tr key={l.id}>
                      <td>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B46C1', fontWeight: 600, padding: 0 }}
                          onClick={() => setViewListing(l)}>{l.title}</button>
                      </td>
                      <td>{l.organisation || '—'}</td>
                      <td>{l.postedByName || l.postedBy || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{l.jobType || '—'}</td>
                      <td>{fmtDate(l.createdAt)}</td>
                      <td style={{ display: 'flex', gap: 4 }}>
                        <button className="bb-btn-sm" disabled={moderating[l.id]}
                          onClick={() => { setReviewPanel(l); setRejectReason(''); }}>Review</button>
                        <button className="bb-btn-primary" style={{ fontSize: 12, padding: '4px 12px' }}
                          disabled={moderating[l.id]} onClick={() => handleApprove(l)}>Approve</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── JOB LISTINGS tab ── */}
        {activeTab === 'live' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ marginLeft: '3ch' }}>
                <h2 style={{ margin: '0 0 4px' }}>Job Listings</h2>
                <p className="bb-editor-desc" style={{ margin: 0 }}>Manage all job listings. Default view shows live listings.</p>
              </div>
              <button className="bb-btn-primary" style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}
                onClick={() => { setShowCreate(true); setCreateForm(BLANK_FORM); setCreateError(''); setCreateSuccess(false); }}>
                + Create Job Listing
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: '3ch' }}>
                <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Type:</label>
                <select className="bb-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
                  <option value="all">All types</option>
                  {JOB_TYPES.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Status:</label>
                <select className="bb-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
                  <option value="all">All statuses</option>
                  {ALL_STATUSES.map(s => (
                    <option key={s} value={s}>{STATUS_BADGE[s]?.label || s}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Posted By:</label>
                <select className="bb-select" value={postedByFilter} onChange={e => setPostedByFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
                  <option value="all">All</option>
                  {postedByOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {loading ? (
              <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={8}>Loading listings…</td></tr></tbody></table>
            ) : filteredListings.length === 0 ? (
              <div className="bb-empty-state">No listings match the selected filters.</div>
            ) : (
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Organisation</th>
                    <th>Type</th>
                    <th>Posted By</th>
                    <th>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}
                        onClick={() => cycleSort(appSort, setAppSort, setDeadlineSort)}>
                        Applications{sortIcon(appSort)}
                      </button>
                    </th>
                    <th>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}
                        onClick={() => cycleSort(deadlineSort, setDeadlineSort, setAppSort)}>
                        Deadline{sortIcon(deadlineSort)}
                      </button>
                    </th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredListings.map(l => {
                    const badge = STATUS_BADGE[l.status] || STATUS_BADGE.pending_approval;
                    return (
                      <tr key={l.id}>
                        <td>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B46C1', fontWeight: 600, padding: 0, textAlign: 'left' }}
                            onClick={() => setViewListing(l)}>{l.title}</button>
                        </td>
                        <td>{l.organisation || '—'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{l.jobType || '—'}</td>
                        <td>{l.postedByName || l.postedBy || '—'}</td>
                        <td>{(l.applications || []).length}</td>
                        <td>{fmtDate(l.deadline)}</td>
                        <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                        <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {l.status === 'live' && (
                            <>
                              <button className="bb-btn-outline-sm" disabled={moderating[l.id]}
                                onClick={() => handleModerateAction(l.id, 'paused')}>Pause</button>
                              <button className="bb-btn-outline-sm" disabled={moderating[l.id]}
                                onClick={() => handleModerateAction(l.id, 'closed')}>Close</button>
                            </>
                          )}
                          {l.status === 'paused' && (
                            <button className="bb-btn-sm" disabled={moderating[l.id]}
                              onClick={() => handleModerateAction(l.id, 'live')}>Reactivate</button>
                          )}
                          {(l.status === 'live' || l.status === 'paused') && (
                            <button className="bb-btn-danger-sm" disabled={moderating[l.id]}
                              onClick={() => { setRemoveTarget(l.id); setRemoveReason(''); }}>Remove</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── JOB APPLICATIONS tab ── */}
        {activeTab === 'applications' && (
          <div>
            <div className="bb-editor-header">
              <h2 style={{ margin: 0 }}>Job Applications</h2>
              <p className="bb-editor-desc">All applications submitted by members across all job listings.</p>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
              <input
                type="text"
                className="bb-input"
                placeholder="Search by applicant name, email, or job title…"
                value={appsSearchText}
                onChange={e => setAppsSearchText(e.target.value)}
                style={{ fontSize: 13, padding: '6px 10px', maxWidth: 320 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Job Listing:</label>
                <select className="bb-select" value={appsListingFilter} onChange={e => setAppsListingFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
                  <option value="all">All listings</option>
                  {appsListingOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {appsLoading ? (
              <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={8}>Loading applications…</td></tr></tbody></table>
            ) : filteredApplications.length === 0 ? (
              <div className="bb-empty-state">
                {allApplications.length === 0 ? 'No applications have been submitted yet.' : 'No applications match the selected filters.'}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Applicant</th>
                      <th>Email</th>
                      <th>Job Title</th>
                      <th>Organisation</th>
                      <th>Posted By</th>
                      <th>Applied</th>
                      <th>Covering Letter</th>
                      <th>Resume / CV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((a, i) => (
                      <tr key={a.id}>
                        <td style={{ color: '#9CA3AF', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{a.memberName}</td>
                        <td style={{ fontSize: 13, color: '#6B7280' }}>{a.memberEmail}</td>
                        <td style={{ fontWeight: 500, color: '#6B46C1' }}>{a.listingTitle || '—'}</td>
                        <td>{a.organisation || '—'}</td>
                        <td>{a.postedByName || a.postedBy || '—'}</td>
                        <td style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
                          {a.createdAt?.toDate ? a.createdAt.toDate().toLocaleDateString('en-SG') : fmtDate(a.createdAt)}
                        </td>
                        <td>
                          {a.coveringLetterUrl
                            ? <a href={a.coveringLetterUrl} target="_blank" rel="noreferrer" style={{ color: '#6B46C1', fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span>📄</span>
                                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.coveringLetterFileName || 'Open'}</span>
                              </a>
                            : <span style={{ color: '#D1D5DB', fontSize: 13 }}>—</span>}
                        </td>
                        <td>
                          {a.resumeUrl
                            ? <a href={a.resumeUrl} target="_blank" rel="noreferrer" style={{ color: '#6B46C1', fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span>📋</span>
                                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.resumeFileName || 'Open'}</span>
                              </a>
                            : <span style={{ color: '#D1D5DB', fontSize: 13 }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </>
  );
}
